/**
 * AGROMX Frontend - Usuario Service
 * Servicio para gestión de usuarios
 */

import api from './api.js';

class UsuarioService {
  /**
   * Listar usuarios con filtros y paginación
   */
  async list(params = {}) {
    const response = await api.get('/usuarios', params);
    return response;
  }

  /**
   * Obtener usuario por ID
   */
  async getById(id) {
    const response = await api.get(`/usuarios/${id}`);
    return response.data;
  }

  /**
   * Crear nuevo usuario
   */
  async create(data) {
    const response = await api.post('/usuarios', data);
    return response.data;
  }

  /**
   * Actualizar usuario
   */
  async update(id, data) {
    const response = await api.put(`/usuarios/${id}`, data);
    return response.data;
  }

  /**
   * Resetear contraseña (genera nueva temporal)
   */
  async resetPassword(id) {
    const response = await api.put(`/usuarios/${id}/reset-password`);
    return response.data;
  }

  /**
   * Alternar estado activo/inactivo
   */
  async toggleActive(id) {
    const response = await api.put(`/usuarios/${id}/toggle-active`);
    return response.data;
  }

  /**
   * Eliminar usuario
   */
  async delete(id) {
    await api.delete(`/usuarios/${id}`);
    return true;
  }

  /**
   * Obtener estadísticas
   */
  async getStats() {
    const response = await api.get('/usuarios/stats');
    return response.data;
  }

  /**
   * Obtener lista de roles disponibles desde el sistema dinámico (BD).
   * Lee `GET /api/roles` (requiere permiso roles:ver) y filtra activos.
   * Sin cache: queremos que cualquier cambio en el panel admin de roles
   * (crear, editar, desactivar) se refleje inmediatamente al recargar la
   * vista de usuarios. El costo de una llamada por render es despreciable.
   * Si la llamada falla (sin permiso, red, etc.) cae a la lista hardcoded
   * histórica como fallback para no romper la UI.
   */
  async getRoles() {
    try {
      const res = await api.get('/roles');
      // El backend envuelve como { success, data: { roles: [...] }, message }.
      // Soportamos también { data: [...] } o el array directo por defensa.
      const list = (Array.isArray(res?.data?.roles) && res.data.roles)
        || (Array.isArray(res?.data) && res.data)
        || (Array.isArray(res) && res)
        || [];
      const activos = list
        .filter(r => r.activo === 1 || r.activo === true)
        .map(r => ({ value: r.clave, label: r.nombre, _nombre: r.nombre }));
      // Guarda un snapshot SOLO para que formatRol() pueda mostrar el label
      // correcto de roles custom en la tabla. Se sobrescribe en cada llamada
      // para mantenerse fresco.
      this._rolesSnapshot = activos;
      return activos;
    } catch (err) {
      console.warn('No se pudieron cargar roles dinámicos, usando fallback hardcoded:', err.message || err);
      return [
        { value: 'admin', label: 'Administrador' },
        { value: 'almacen', label: 'Almacén' },
        { value: 'produccion', label: 'Producción' },
        { value: 'ventas', label: 'Ventas' },
        { value: 'posventa', label: 'Posventa' },
        { value: 'calidad', label: 'Calidad' },
        { value: 'distribuidor', label: 'Distribuidor' },
        { value: 'compras', label: 'Compras' }
      ];
    }
  }

  /**
   * Formatear nombre de rol para mostrar.
   * Prioriza el snapshot de roles dinámicos (si ya se cargó al menos una vez),
   * después cae al mapa hardcoded histórico, y finalmente devuelve el clave
   * tal cual. El snapshot se refresca cada vez que getRoles() se llama.
   */
  formatRol(rol) {
    if (this._rolesSnapshot) {
      const match = this._rolesSnapshot.find(r => r.value === rol);
      if (match) return match.label;
    }
    const roles = {
      admin: 'Administrador',
      almacen: 'Almacén',
      produccion: 'Producción',
      ventas: 'Ventas',
      posventa: 'Posventa',
      calidad: 'Calidad',
      distribuidor: 'Distribuidor',
      compras: 'Compras'
    };
    return roles[rol] || rol;
  }

  /**
   * Obtener color del badge según el rol
   */
  getRolBadgeClass(rol) {
    const classes = {
      admin: 'badge-danger',
      almacen: 'badge-primary',
      produccion: 'badge-warning',
      ventas: 'badge-success',
      posventa: 'badge-info',
      calidad: 'badge-secondary',
      distribuidor: 'badge-dark',
      compras: 'badge-primary'
    };
    return classes[rol] || 'badge-secondary';
  }

  /**
   * Formatear fecha para mostrar
   */
  formatDate(dateString) {
    if (!dateString) return 'Nunca';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-MX', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }
}

export default new UsuarioService();
